import React from 'react';
import { FormSpy, AnyObject } from 'react-final-form';
import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ContactMailIcon from '@material-ui/icons/ContactMail';
import { Address, AddressSelector, PlaceType } from '../../selectors/AddressSelector';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OtherPartyAddressPanel');

// Explicit request (EnquiryFormDrawer split): the other side's "Address" accordion.
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface OtherPartyAddressPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    defaultExpanded: boolean;
    homeCountry: string;
    getAddressLabelForPanel: (buildingName: string, level: string, streetNumber: string, street1: string, street2: string, suburb: string, addressState: string, postCode: string, country: string) => string;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
}

interface OtherPartyAddressPanelBodyProps extends OtherPartyAddressPanelProps {
    formValues: AnyObject;
}

const OtherPartyAddressPanelBody: React.FC<OtherPartyAddressPanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, defaultExpanded, homeCountry, getAddressLabelForPanel, createAddressSelectionHandler, createInputChangedHandler,
        formValues,
    } = bodyProps;
    const values = formValues;

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
        // disabled={disabledAddress(values)}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="other-party-address-content"
                id="other-party-address-header"
                className={classes.AccordionSummary}
            >
                <Typography className={classes.heading}>
                    <ContactMailIcon className={classes.headingIcon} />
                    Main Address
                </Typography>

                <div className={classes.infoContainer}>
                    <div className="col">
                        <div className="value">
                            {getAddressLabelForPanel(
                                values.otherPartyAddressName,
                                values.otherPartyFloor,
                                values.otherPartyNumber,
                                values.otherPartyStreet,
                                values.otherPartyStreet2,
                                values.otherPartySuburb,
                                values.otherPartyState,
                                values.otherPartyPostcode,
                                values.otherPartyCountry
                            )}
                        </div>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} md={6} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyAddressName"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Building name"
                                    type="text"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    disabled={values.otherPartyNameGuid}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Building name is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={3} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyFloor"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Unit/Level"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Unit/Level is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={3} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyNumber"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Number"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Number is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyStreet"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Street 1"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    name="otherPartyStreet"
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Street 1 is required'
                                            : ''
                                    }
                                    value={values.otherPartyStreet}
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'otherPartyStreet')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyStreet2"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <AddressSelector
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    freeSolo={true}
                                    label="Street 2"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    name="otherPartyStreet2"
                                    homeCountry={homeCountry}
                                    // tslint:disable-next-line: max-line-length
                                    // placeTypes={['geocode', 'establishment']} // ['geocode', 'address', 'establishment', '(regions)', '(cities)'];
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Street 2 is required'
                                            : ''
                                    }
                                    onSelection={(placeType: PlaceType | null, address: Address, name: string) =>
                                                createAddressSelectionHandler(form, form.getState().values)(placeType, address, name)}
                                    onInputChanged={createInputChangedHandler(form, 'otherPartyStreet2')}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartySuburb"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="City/Suburb"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'City/Suburb is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyState"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="State"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'State is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPostcode"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Postal Code"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Postal Code is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyCountry"
                            subscription={{ touched: true, error: true, value: true }}
                        // validate={required}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Country"
                                    type="text"
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Country is required'
                                            : ''
                                    }
                                    inputProps={{
                                        autoComplete: 'new-password'
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OtherPartyAddressPanelBody.displayName = 'OtherPartyAddressPanelBody';

export const OtherPartyAddressPanel: React.FC<OtherPartyAddressPanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <OtherPartyAddressPanelBody
                {...panelProps}
                formValues={{
                    otherPartyAddressName: values.otherPartyAddressName,
                    otherPartyFloor: values.otherPartyFloor,
                    otherPartyNumber: values.otherPartyNumber,
                    otherPartyStreet: values.otherPartyStreet,
                    otherPartyStreet2: values.otherPartyStreet2,
                    otherPartySuburb: values.otherPartySuburb,
                    otherPartyState: values.otherPartyState,
                    otherPartyPostcode: values.otherPartyPostcode,
                    otherPartyCountry: values.otherPartyCountry,
                    otherPartyNameGuid: values.otherPartyNameGuid,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
OtherPartyAddressPanel.displayName = 'OtherPartyAddressPanel';
